import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { Check, ArrowRight, CreditCard, Heart, RefreshCw, Shield, Zap, Users } from 'lucide-react'
import { AppSignupLink } from '@/components/AppSignupLink'
import { capability, dfyBundle } from '@gjl/voice/pricing'

const donorManagement = capability('donor-management')!
const concierge = dfyBundle('concierge')

const features = [
  {
    icon: CreditCard,
    title: 'Powered by Stripe',
    description:
      'Accept credit cards, debit cards, and digital wallets securely. Industry-leading payment processing trusted by millions.',
  },
  {
    icon: RefreshCw,
    title: 'Recurring Donations',
    description:
      'Let supporters give monthly or annually. Predictable recurring revenue helps you plan ahead and grow sustainably.',
  },
  {
    icon: Heart,
    title: 'Custom Donation Amounts',
    description:
      'Set suggested amounts or let donors choose their own. Make it easy for people to support at any level.',
  },
  {
    icon: Shield,
    title: 'Automatic Receipts',
    description: 'Donors receive instant email receipts. Tax-ready documentation without manual work from your team.',
  },
  {
    icon: Users,
    title: 'Donor Management',
    description:
      'Track all donations in one place. See who supports you, how much they have given, and when they last donated.',
  },
  {
    icon: Zap,
    title: 'No Platform Fees',
    description: 'We do not take a cut of your donations. You only pay Stripe payment processing fees (2.9% + $0.30).',
  },
]

const howItWorks = [
  {
    step: '1',
    title: 'Connect Stripe',
    description: 'Link your Stripe account in minutes. If you do not have one, create a free account during setup.',
  },
  {
    step: '2',
    title: 'Add donation widget',
    description: 'Add the donation widget to your website. Customize amounts, messaging, and design.',
  },
  {
    step: '3',
    title: 'Start receiving support',
    description: 'Donations go directly to your bank account via Stripe. Track everything in your dashboard.',
  },
]

const otherFeatures = [
  { title: 'Website', href: '/platform/features/website', description: 'Build your site' },
  { title: 'Ticketing', href: '/platform/features/events', description: 'Manage concerts' },
  { title: 'Email', href: '/platform/features/email', description: 'Send newsletters' },
  { title: 'Analytics', href: '/platform/features/analytics', description: 'Track growth' },
  { title: 'Applications', href: '/platform/features/applications', description: 'Run auditions' },
]

export default function DonationsFeaturePage() {
  useSEO({
    title: 'Donor Management for Music Organizations | Fugue Lab App',
    description:
      'Recurring giving, tax receipts, campaigns and donor records in the Fugue Lab app, on the same contact record as your ticket buyers and subscribers.',
    url: '/platform/features/donations',
    canonical: '/platform/features/donations',
  })

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const featuresRef = useStaggeredGrid<HTMLDivElement>('.feature-card', { stagger: 0.1 })
  const stepsRef = useScrollReveal<HTMLDivElement>('.step-item', { stagger: 0.15 })
  const pricingRef = useStaggeredGrid<HTMLDivElement>('.pricing-card', { stagger: 0.15 })
  const otherFeaturesRef = useStaggeredGrid<HTMLDivElement>('.other-feature-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="px-4 py-20">
        <div ref={heroRef} className="container mx-auto max-w-4xl text-center">
          <div className="bg-primary/10 text-primary mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
            <CreditCard className="h-4 w-4" />
            Donor Management
          </div>
          <h1 className="mb-6 text-4xl font-bold md:text-5xl">
            Accept donations
            <br />
            the easy way
          </h1>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-xl">
            Let supporters give to your organization online. One-time or recurring donations, powered by Stripe. No
            platform fees, just standard payment processing.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <AppSignupLink>
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Start Accepting Donations <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </m.div>
            </AppSignupLink>
            <Link to="/platform">
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 text-lg">
                  See All Features
                </Button>
              </m.div>
            </Link>
          </div>
          <p className="text-muted-foreground mt-4 text-sm">Free to set up. Only pay Stripe fees (2.9% + $0.30).</p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-surface px-4 py-20">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Everything You Need for Fundraising</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              Professional donation capabilities without the complexity or extra fees.
            </p>
          </div>
          <div ref={featuresRef} className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <m.div
                key={feature.title}
                className="feature-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <feature.icon className="text-primary mb-4 h-10 w-10" />
                <h3 className="mb-2 text-lg font-semibold">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* No Platform Fees */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="mb-6 text-3xl font-bold">More Money for Your Mission</h2>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-xl">
            Unlike other fundraising platforms, we do not take a percentage of your donations. 100% of every gift goes
            to your organization (minus standard Stripe fees).
          </p>
          <div className="bg-card border-border mx-auto max-w-md rounded-2xl border p-8">
            <div className="mb-6 text-center">
              <p className="text-muted-foreground mb-2 text-sm">Example: $100 donation</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Donation amount</span>
                  <span className="font-semibold">$100.00</span>
                </div>
                <div className="text-muted-foreground flex justify-between">
                  <span>Stripe processing</span>
                  <span>-$3.20</span>
                </div>
                <div className="text-muted-foreground flex justify-between">
                  <span>Fugue Lab fee</span>
                  <span className="text-green-500">$0.00</span>
                </div>
                <div className="border-border flex justify-between border-t pt-2 font-semibold">
                  <span>You receive</span>
                  <span className="text-green-500">$96.80</span>
                </div>
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              Compare to other platforms that charge 3-8% platform fees on top of payment processing.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-surface px-4 py-20">
        <div ref={stepsRef} className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Get Started in Minutes</h2>
          </div>
          <div className="space-y-8">
            {howItWorks.map((item) => (
              <div key={item.step} className="step-item flex gap-6">
                <m.div
                  className="bg-primary text-primary-foreground flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-xl font-bold"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  {item.step}
                </m.div>
                <div>
                  <h3 className="mb-2 text-xl font-semibold">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Simple Pricing</h2>
            <p className="text-muted-foreground">
              Accepting donations is free. Donor Management is the paid layer on top.
            </p>
          </div>
          <div ref={pricingRef} className="mx-auto grid max-w-3xl gap-8 md:grid-cols-2">
            <m.div
              className="pricing-card border-primary/50 bg-primary/5 rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary mb-2 text-sm font-medium">0% platform fee</div>
              <h3 className="mb-2 text-2xl font-bold">Accept Donations</h3>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold">Free</span>
                <span className="text-muted-foreground">forever</span>
              </div>
              <ul className="mb-8 space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Donate button on your site</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Money goes straight to your own Stripe account</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">We take 0% of every gift</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Only Stripe processing applies (2.9% + $0.30)</span>
                </li>
              </ul>
              <Link to="/get-started" className="block">
                <m.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button className="w-full cursor-pointer">Get Started</Button>
                </m.div>
              </Link>
            </m.div>
            <m.div
              className="pricing-card border-border bg-card rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary mb-2 text-sm font-medium">Set up and run by our team</div>
              <h3 className="mb-2 text-2xl font-bold">{donorManagement.name}</h3>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold">${donorManagement.priceMonthly}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <ul className="mb-8 space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Recurring giving</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Automated tax receipts</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Campaigns and donor records</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Our team sets it up and runs it for you today</span>
                </li>
              </ul>
              <p className="text-muted-foreground mb-6 text-sm">
                Want the whole fundraising program handled?{' '}
                <Link to="/services/done-for-you#concierge" className="text-primary hover:underline">
                  {concierge.name} covers it at {concierge.price}
                </Link>
                .
              </p>
              <Link to="/services/done-for-you#concierge" className="block">
                <m.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button variant="outline" className="w-full cursor-pointer">
                    See Done-For-You Options
                  </Button>
                </m.div>
              </Link>
            </m.div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/pricing" className="text-primary font-medium hover:underline">
              See pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Other Features */}
      <section className="bg-surface px-4 py-16">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold">More Features</h2>
            <p className="text-muted-foreground">Donor Management is just the beginning.</p>
          </div>
          <div ref={otherFeaturesRef} className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {otherFeatures.map((feature) => (
              <m.div
                key={feature.href}
                className="other-feature-card"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Link
                  to={feature.href}
                  className="border-border bg-card hover:border-primary block h-full rounded-lg border p-4 text-center transition-colors"
                >
                  <h3 className="font-semibold">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Link>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary/5 px-4 py-16">
        <div ref={ctaRef} className="container mx-auto max-w-3xl text-center">
          <div className="cta-content">
            <h2 className="mb-4 text-3xl font-bold">Ready to accept donations?</h2>
            <p className="text-muted-foreground mb-8 text-xl">
              Join music organizations using Fugue Lab to fund their mission.
            </p>
            <AppSignupLink>
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="inline-block"
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Start Accepting Donations <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </m.div>
            </AppSignupLink>
          </div>
        </div>
      </section>
    </div>
  )
}
